import { usePersonnel } from 'features/Responder/features/Personnel';
import { useSchedules } from 'features/Responder/features/Schedules/contexts/SchedulesContext';
import { useCallback, useMemo } from 'react';
import {
  generateAllOccurrencesRRule,
  generateAssignmentRRule,
  generateWeeklyAssignmentRRuleForDay,
} from 'utils/recurrenceUtils';
import { useAssignments } from '../../../../contexts/AssignmentsContext';
import { PersonnelOptions } from './components/PersonnelOptions';

/**
 * AssignmentAssigner
 *
 * Renders a list of all personnel with two mutually-exclusive checkboxes
 * per person:
 *   • "Assign to All"  — creates/keeps an assignment with recurrence: null
 *                        (applies to every occurrence of the schedule)
 *   • "Assign to This" — creates/keeps an assignment whose recurrence is a
 *                        single-occurrence RRule exactly matching this event
 *
 * Selecting one option automatically deselects the other.
 * When neither is checked the person is not assigned.
 */
export const AssignmentAssigner = ({ event, source = '' }) => {
  // Constants for generating rrules and labels for the "Specific Days of Week" options.
  const JS_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const JS_DAYS_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  const { personnel } = usePersonnel();
  const { activeEditAssignments, setActiveEditAssignments } = useAssignments();
  const { activeEditSchedule } = useSchedules();

  /**
   * RRule for the single "This Occurrence Only" scoped option, available only
   * in calendar view. Null in all pattern views.
   */
  const thisOccurrenceRRule = useMemo(() => {
    if (!event?.start || !activeEditSchedule?.recurrence) return null;
    if (source === 'calendar')
      return generateAssignmentRRule(event, activeEditSchedule, 'calendar');
    return null;
  }, [source, event, activeEditSchedule]);

  /**
   * RRule string that covers every future occurrence of the schedule at the
   * same time slot as the selected event (no COUNT / UNTIL limit).
   * This replaces the old `recurrence: null` sentinel for "Assign to All".
   */
  const allOccurrencesRRule = useMemo(() => {
    if (!event?.start || !activeEditSchedule?.recurrence) return null;
    return generateAllOccurrencesRRule(event, activeEditSchedule);
  }, [event, activeEditSchedule]);

  /** All 7 weekday options for the "Specific Days of Week" picker. */
  const allWeekdayOptions = useMemo(() => {
    if (!event?.start || !activeEditSchedule?.recurrence) return [];
    return [0, 1, 2, 3, 4, 5, 6].map((jsDay) => ({
      jsDay,
      label: JS_DAYS[jsDay],
      short: JS_DAYS_SHORT[jsDay],
      rrule: generateWeeklyAssignmentRRuleForDay(event, activeEditSchedule, jsDay),
    }));
  }, [event, activeEditSchedule]);

  /**
   * Returns the current assignment mode for a user:
   *   'all'  — assigned to all occurrences at this time slot (matches allOccurrencesRRule,
   *             or legacy recurrence === null)
   *   'this' — assigned only to this specific occurrence
   *   null   — not assigned
   */
  const getMode = useCallback(
    (userId) => {
      if (!activeEditAssignments || !activeEditSchedule) return null;
      const list = activeEditAssignments[userId.toString()] ?? [];
      const active = list.filter(
        (a) => a.definition_id === activeEditSchedule.id && a.enabled !== false,
      );
      if (
        active.some(
          (a) =>
            a.recurrence === null || (allOccurrencesRRule && a.recurrence === allOccurrencesRRule),
        )
      )
        return 'all';
      if (thisOccurrenceRRule && active.some((a) => a.recurrence === thisOccurrenceRRule))
        return 'this';
      return null;
    },
    [activeEditAssignments, activeEditSchedule, allOccurrencesRRule, thisOccurrenceRRule],
  );

  /**
   * Returns a Set of weekday rrule strings currently active for the given user.
   *
   * State always stores atomic single-day entries (multi-day rrules are decomposed
   * at load time in buildMappedAssignmentsForSchedule), so this is a simple exact
   * match. For display purposes we show all of the user's assigned days when the
   * current event's day is itself one of those days, so e.g. a Saturday card shows
   * "Su We Sa" not just "Sa".
   */
  const getActiveWeekdayRrules = useCallback(
    (userId) => {
      if (!activeEditAssignments || !activeEditSchedule) return new Set();
      const list = activeEditAssignments[userId.toString()] ?? [];
      const weekdayRruleSet = new Set(allWeekdayOptions.map((o) => o.rrule));

      // Collect all active single-day rrules for this user + schedule.
      const userWeekdayRrules = new Set(
        list
          .filter(
            (a) =>
              a.definition_id === activeEditSchedule.id &&
              a.enabled !== false &&
              weekdayRruleSet.has(a.recurrence),
          )
          .map((a) => a.recurrence),
      );

      if (userWeekdayRrules.size === 0) return new Set();

      // Pattern view: always show all assigned days.
      if (source.startsWith('pattern')) return userWeekdayRrules;

      // Calendar view: only show the full set when the event's actual day is
      // one of the assigned days. Non-covered days show "Not assigned".
      const eventJsDay = event?.start instanceof Date ? event.start.getDay() : null;
      if (eventJsDay === null) return userWeekdayRrules;
      const eventDayOpt = allWeekdayOptions.find((o) => o.jsDay === eventJsDay);
      if (!eventDayOpt) return new Set();
      return userWeekdayRrules.has(eventDayOpt.rrule) ? userWeekdayRrules : new Set();
    },
    [activeEditAssignments, activeEditSchedule, allWeekdayOptions, source, event],
  );

  /**
   * Remove or mark-for-deletion a specific assignment entry.
   * - If the entry has no backend id it was never persisted, so drop it entirely.
   * - If it has an id, keep it as enabled:false so uploadAssignments will DELETE it.
   */
  const purgeEntry = useCallback(
    (list, predicate) =>
      list.reduce((acc, a) => {
        if (!predicate(a)) {
          acc.push(a);
        } else if (a.id) {
          // Exists on the backend — mark for deletion on next upload.
          acc.push({ ...a, enabled: false });
        }
        // else: no id, never saved → drop silently.
        return acc;
      }, []),
    [],
  );

  /**
   * Set the assignment mode for a user.
   * - 'all': clears all scoped entries and adds a null-recurrence entry (or toggles it off).
   * - scoped: toggles just this event's rrule independently; multiple scoped rrules
   *   (e.g. Thursday + Friday) can coexist. Selecting a scoped option while 'all' is
   *   active removes the 'all' entry first.
   */
  const handleModeSelect = useCallback(
    (userId, selectedMode) => {
      if (!activeEditAssignments || !activeEditSchedule) return;

      const key = userId.toString();
      const currentList = activeEditAssignments[key] ?? [];

      if (selectedMode === 'all') {
        if (!allOccurrencesRRule) return;
        const currentMode = getMode(userId);
        if (currentMode === 'all') {
          // Deselect 'all' only — purge both the new rrule and any legacy null entry.
          const purged = purgeEntry(
            currentList,
            (a) =>
              a.definition_id === activeEditSchedule.id &&
              (a.recurrence === null || a.recurrence === allOccurrencesRRule),
          );
          setActiveEditAssignments({ ...activeEditAssignments, [key]: purged });
          return;
        }
        // Switch to 'all': remove all scoped entries (including any legacy null entry),
        // keeping only any existing allOccurrencesRRule that can be re-enabled.
        const withoutScoped = purgeEntry(
          currentList,
          (a) => a.definition_id === activeEditSchedule.id && a.recurrence !== allOccurrencesRRule,
        );
        const existingAll = withoutScoped.find(
          (a) => a.definition_id === activeEditSchedule.id && a.recurrence === allOccurrencesRRule,
        );
        if (existingAll) {
          setActiveEditAssignments({
            ...activeEditAssignments,
            [key]: withoutScoped.map((a) =>
              a === existingAll ? { ...a, recurrence: allOccurrencesRRule, enabled: true } : a,
            ),
          });
        } else {
          setActiveEditAssignments({
            ...activeEditAssignments,
            [key]: [
              ...withoutScoped,
              {
                personnel_id: userId,
                definition_id: activeEditSchedule.id,
                recurrence: allOccurrencesRRule,
                notes: null,
                enabled: true,
              },
            ],
          });
        }
        return;
      }

      // Scoped mode — only 'this' (This Occurrence Only) is supported.
      if (selectedMode !== 'this' || !thisOccurrenceRRule) {
        console.warn('AssignmentAssigner: unknown or unsupported selectedMode', selectedMode);
        return;
      }
      const targetRRule = thisOccurrenceRRule;

      // Check if this specific rrule is already active → toggle it off.
      const alreadyActive = currentList.some(
        (a) =>
          a.definition_id === activeEditSchedule.id &&
          a.recurrence === targetRRule &&
          a.enabled !== false,
      );
      if (alreadyActive) {
        const purged = purgeEntry(
          currentList,
          (a) => a.definition_id === activeEditSchedule.id && a.recurrence === targetRRule,
        );
        setActiveEditAssignments({ ...activeEditAssignments, [key]: purged });
        return;
      }

      // Add this scoped entry. Remove 'all' if present, but keep other scoped rrules.
      // Exception: 'this' (single occurrence) is mutually exclusive with weekday patterns,
      // so also purge all active weekday rrules when activating it.
      const weekdayRruleSet = new Set(allWeekdayOptions.map((o) => o.rrule));
      const withoutAll = purgeEntry(
        currentList,
        (a) =>
          a.definition_id === activeEditSchedule.id &&
          (a.recurrence === null ||
            a.recurrence === allOccurrencesRRule ||
            (selectedMode === 'this' && weekdayRruleSet.has(a.recurrence))),
      );
      setActiveEditAssignments({
        ...activeEditAssignments,
        [key]: [
          ...withoutAll,
          {
            personnel_id: userId,
            definition_id: activeEditSchedule.id,
            recurrence: targetRRule,
            notes: null,
            enabled: true,
          },
        ],
      });
    },
    [
      activeEditAssignments,
      activeEditSchedule,
      allOccurrencesRRule,
      allWeekdayOptions,
      thisOccurrenceRRule,
      getMode,
      purgeEntry,
      setActiveEditAssignments,
    ],
  );

  /**
   * Toggle a single weekday rrule on or off for a user.
   * State always holds atomic single-day entries (decomposed at load time), so
   * this is a straightforward exact-match add/remove. mergeAssignmentRRules
   * recombines them into a multi-day rrule when uploading.
   */
  const handleWeekdayToggle = useCallback(
    (userId, targetRRule) => {
      if (!activeEditAssignments || !activeEditSchedule || !targetRRule) return;
      const key = userId.toString();
      const currentList = activeEditAssignments[key] ?? [];
      const alreadyActive = currentList.some(
        (a) =>
          a.definition_id === activeEditSchedule.id &&
          a.recurrence === targetRRule &&
          a.enabled !== false,
      );
      if (alreadyActive) {
        const purged = purgeEntry(
          currentList,
          (a) => a.definition_id === activeEditSchedule.id && a.recurrence === targetRRule,
        );
        setActiveEditAssignments({ ...activeEditAssignments, [key]: purged });
      } else {
        // Remove 'all' if present; keep all other weekday rrules.
        const withoutAll = purgeEntry(
          currentList,
          (a) =>
            a.definition_id === activeEditSchedule.id &&
            (a.recurrence === null || a.recurrence === allOccurrencesRRule),
        );
        setActiveEditAssignments({
          ...activeEditAssignments,
          [key]: [
            ...withoutAll,
            {
              personnel_id: userId,
              definition_id: activeEditSchedule.id,
              recurrence: targetRRule,
              notes: null,
              enabled: true,
            },
          ],
        });
      }
    },
    [
      activeEditAssignments,
      activeEditSchedule,
      allOccurrencesRRule,
      purgeEntry,
      setActiveEditAssignments,
    ],
  );

  /** Remove all weekday-rrule assignments for a user. */
  const handleClearAllWeekdays = useCallback(
    (userId) => {
      if (!activeEditAssignments || !activeEditSchedule) return;
      const key = userId.toString();
      const currentList = activeEditAssignments[key] ?? [];
      const weekdayRruleSet = new Set(allWeekdayOptions.map((o) => o.rrule));
      const purged = purgeEntry(
        currentList,
        (a) => a.definition_id === activeEditSchedule.id && weekdayRruleSet.has(a.recurrence),
      );
      setActiveEditAssignments({ ...activeEditAssignments, [key]: purged });
    },
    [
      activeEditAssignments,
      activeEditSchedule,
      allWeekdayOptions,
      purgeEntry,
      setActiveEditAssignments,
    ],
  );

  if (!event) {
    console.warn('AssignmentAssigner rendered without an event prop');
    return null;
  }

  return (
    <div className="d-flex flex-column gap-2">
      <hr className="border-white opacity-50 m-0" />
      <h5>Assigned Personnel</h5>
      <div className="d-flex flex-column gap-2">
        {Object.values(personnel).map((person) => (
          <PersonnelOptions
            key={person.id}
            person={person}
            mode={getMode(person.userId)}
            thisOccurrenceRRule={thisOccurrenceRRule}
            onModeSelect={handleModeSelect}
            weekdayOptions={allWeekdayOptions}
            activeWeekdayRrules={getActiveWeekdayRrules(person.userId)}
            onWeekdayToggle={handleWeekdayToggle}
            onClearWeekdays={handleClearAllWeekdays}
          />
        ))}
      </div>
    </div>
  );
};
